import {
  WorkspacesPostRequest,
  WorkspacesWorkspaceIdPatchRequest,
  WorkspacesWorkspaceIdDeleteRequest,
  WorkspacesWorkspaceIdMembersPostRequest,
  WorkspacesWorkspaceIdMembersMemberIdDeleteRequest,
  WorkspacesWorkspaceIdInvitationsPostRequest,
  WorkspacesWorkspaceIdInvitationsInvitationIdPatchRequest,
  WorkspacesWorkspaceIdInvitationsInvitationIdDeleteRequest,
  WorkspacesWorkspaceIdMembersMemberIdPatchRequest,
  SmepWorkspaceRoleRamMemberPaginatedList,
} from '@nativewaves/nw-cloud-sdk/smep'
import { InfiniteData, useQueryClient, useMutation } from '@tanstack/react-query'
import { useMatchRoute } from '@tanstack/react-router'

import { useRecentWorkspaces } from 'domains/_Sidebar/Sections/ContentWorkspaces/Workspace'
import { environmentRoute } from 'pages/NWPlatform'
import { SmepAPI } from 'services/api'
import { workspaceQueryKeys } from 'services/queryKeys'
import { updateListCachesByDeletion } from 'utils'

export const useWorkspaceCreateMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: WorkspacesPostRequest) => SmepAPI.workspaces.workspacesPost(params),
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: workspaceQueryKeys.lists() })
    },
  })
}

export const useWorkspaceUpdateMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: WorkspacesWorkspaceIdPatchRequest) => SmepAPI.workspaces.workspacesWorkspaceIdPatch(params),
    onSuccess: () => {
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: workspaceQueryKeys.lists() }),
        queryClient.invalidateQueries({ queryKey: workspaceQueryKeys.details() }),
      ])
    },
  })
}

export const useWorkspaceDeleteMutation = () => {
  const queryClient = useQueryClient()

  const matchRoute = useMatchRoute()

  const [recentWorkspaces, setRecentWorkspaces] = useRecentWorkspaces()
  const environmentRouteMatch = matchRoute({ to: environmentRoute.to, fuzzy: true })

  return useMutation({
    mutationFn: (params: WorkspacesWorkspaceIdDeleteRequest) => SmepAPI.workspaces.workspacesWorkspaceIdDelete(params),
    onSuccess: (_, variables) => {
      if (
        environmentRouteMatch &&
        recentWorkspaces[environmentRouteMatch.environmentId]!.includes(variables.workspaceId)
      ) {
        setRecentWorkspaces({
          environmentId: environmentRouteMatch.environmentId,
          workspaceId: variables.workspaceId,
        })
      }
      return queryClient.invalidateQueries({ queryKey: workspaceQueryKeys.lists() })
    },
  })
}

export const useWorkspaceMemberCreateMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: WorkspacesWorkspaceIdMembersPostRequest) =>
      SmepAPI.workspaces.workspacesWorkspaceIdMembersPost(params),
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: workspaceQueryKeys.memberLists() })
    },
  })
}

export const useWorkspaceMemberUpdateMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: WorkspacesWorkspaceIdMembersMemberIdPatchRequest) =>
      SmepAPI.workspaces.workspacesWorkspaceIdMembersMemberIdPatch(params),
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: workspaceQueryKeys.memberLists() })
    },
  })
}

export const useWorkspaceMemberDeleteMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: WorkspacesWorkspaceIdMembersMemberIdDeleteRequest) =>
      SmepAPI.workspaces.workspacesWorkspaceIdMembersMemberIdDelete(params),
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: workspaceQueryKeys.memberLists() })
    },
    onSettled: (_, error, variables) => {
      if (!error) {
        queryClient.setQueriesData<InfiniteData<SmepWorkspaceRoleRamMemberPaginatedList>>(
          { queryKey: workspaceQueryKeys.memberLists() },
          updateListCachesByDeletion({ idKey: 'id', idToMatch: variables.memberId }),
        )
      }
    },
  })
}

export const useWorkspaceInvitationCreateMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: WorkspacesWorkspaceIdInvitationsPostRequest) =>
      SmepAPI.workspaces.workspacesWorkspaceIdInvitationsPost(params),
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: workspaceQueryKeys.invitationLists() })
    },
  })
}

export const useWorkspaceInvitationUpdateMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['smep', 'workspace', 'invitation', 'PATCH'] as const,
    mutationFn: (params: WorkspacesWorkspaceIdInvitationsInvitationIdPatchRequest) =>
      SmepAPI.workspaces.workspacesWorkspaceIdInvitationsInvitationIdPatch(params),
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: workspaceQueryKeys.invitationLists() })
    },
  })
}

export const useWorkspaceInvitationDeleteMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: WorkspacesWorkspaceIdInvitationsInvitationIdDeleteRequest) =>
      SmepAPI.workspaces.workspacesWorkspaceIdInvitationsInvitationIdDelete(params),
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: workspaceQueryKeys.invitationLists() })
    },
  })
}
